/**
 * Files dropped from the desktop onto the canvas or a folder (or pasted on
 * the canvas, `source: 'paste'`): refuse a
 * read-only destination, judge the rest by the shared intake (a file over the
 * upload cap is refused by name; past one import's cap the first ones are kept
 * and the rest counted), reported in the intake's words, then hand them to
 * today's Import flow (`useImportFiles`), so the resumable upload, the tray
 * and processing stay exactly as they are.
 */

import { useCallback } from 'react';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { IMPORT_MAX_FILES, useImportFiles } from '../../../hooks/upload/useImportFiles';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { safeAnalytics } from '../../../utils/analytics';
import { countBucket } from '../../../utils/analyticsCountBucket';
import { ANY_FILE, typeImageByName } from '../../upload/intake/rules';
import { useIntakeReport } from '../../upload/intake/useFileIntake';
import { validateFiles } from '../../upload/intake/validateFiles';
import { readOnlyRefusal, type DropFolder } from './dropRules';

export function useDropUpload() {
  const workspaceId = useWorkspaceId();
  const importFiles = useImportFiles();
  const toast = useAppToast();
  const report = useIntakeReport();

  return useCallback(
    (files: File[], target: DropFolder, source: 'drop' | 'paste' = 'drop'): void => {
      if (files.length === 0 || !workspaceId) return;
      if (!target.canEdit) {
        toast({
          title: 'Nothing uploaded',
          description: readOnlyRefusal(target),
          status: 'warning',
        });
        return;
      }
      const outcome = validateFiles(files, ANY_FILE, { maxFiles: IMPORT_MAX_FILES });
      report(outcome, source, { maxFiles: IMPORT_MAX_FILES });
      const { accepted } = outcome;
      if (accepted.length === 0) return;
      safeAnalytics.track(ANALYTICS_EVENTS.DROP_UPLOAD_STARTED, {
        file_count_bucket: countBucket(accepted.length),
        target: target.id ? 'folder' : 'canvas',
        source,
      });
      importFiles(accepted.map(typeImageByName), {
        parent: target.id
          ? { type: 'project', id: target.id }
          : { type: 'workspace', id: workspaceId },
        trigger: source === 'paste' ? 'paste' : 'drag_drop',
      });
    },
    [importFiles, report, toast, workspaceId],
  );
}
